// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// COMO O PLANEADOR LÊ UM AUTOCARRO E UM DIA — uma formulação, servidor e ecrã (2026-10-30)
// ============================================================================
//
// 📗 Cliente, 2026-10-30, sobre o Diário: *«moveu 8 dias de V0167 para V0164 · V0167 · 29 Out a 4
//    Nov» — às vezes não corresponde ao número que está no mapa na primeira coluna, e tem de o ser
//    em toda a app, a fim de ser fácil verificar o que aconteceu e não confundir.»*
//
// A REGRA: o planeador nunca vê um identificador interno.
//   · o autocarro diz-se pelo NÚMERO DA FROTA (a primeira coluna do mapa, `numero_interno`), e
//     nunca pelo id (`V0167`); um subcontratado diz-se pelo NOME;
//   · o dia diz-se pela DATA («2 Fev», «29 Out a 4 Nov»), e nunca pelo índice da fita
//     («dia 202»);
//   · 📗 2026-10-30 (aprovado pelo cliente) · o MOTORISTA diz-se pelo NOME, como no mapa — o
//     diminutivo, ou o nome completo quando não há (os condutores reais da GVA não o têm) — e
//     nunca pelo id (`M0048`). A mesma troca e o mesmo mapa de nomes: uma só formulação.
//
// ⚠️ Antes desta data havia QUATRO formas de escrever uma data curta (o mapa «2 Fev», a edição
//    «02 Fev», o travão «02/11», a recomendação do intervalo «2/11»). A que divergisse era a que
//    ninguém olhava. Passam todas por aqui.
// ============================================================================
import { dataDoIndice } from './janela-do-mapa.js';
/** Os meses como o mapa os escreve. ⚠️ À mão e não por `toLocaleDateString` — ver `diaCurtoDaData`. */
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
/**
 * `AAAA-MM-DD` → «2 Fev». ⚠️ Escrito à mão: com `month: 'short'` alguns ambientes devolvem `2/01`,
 * que se lê «2 de Janeiro» em Portugal e «Fevereiro, dia 1» em metade do mundo.
 */
export function diaCurtoDaData(isoData) {
    const [, m, d] = isoData.slice(0, 10).split('-');
    const mes = MESES[Number(m) - 1];
    if (d === undefined || mes === undefined)
        return isoData;
    return `${String(Number(d))} ${mes}`;
}
/** O índice do dia na fita → «2 Fev». */
export function diaCurto(janela, dia) {
    return diaCurtoDaData(dataDoIndice(janela, dia));
}
/** Um período em índices → «2 Fev» (um dia) ou «29 Out a 4 Nov». */
export function periodoCurto(janela, dia0, dia1) {
    if (dia0 === dia1)
        return diaCurto(janela, dia0);
    return `${diaCurto(janela, dia0)} a ${diaCurto(janela, dia1)}`;
}
/** Vários dias soltos → os pedaços contínuos em datas: «10 a 14 Nov, 20 Nov». */
export function diasCurtos(janela, dias) {
    const ordenados = [...new Set(dias)].sort((a, b) => a - b);
    const partes = [];
    let i = 0;
    while (i < ordenados.length) {
        const ini = ordenados[i];
        let fim = ini;
        while (ordenados[i + 1] === fim + 1) {
            i++;
            fim = ordenados[i];
        }
        partes.push(periodoCurto(janela, ini, fim));
        i++;
    }
    return partes.join(', ');
}
/** O id de um executor → o nome que o planeador lê. Um id desconhecido fica como está. */
export function nomeDoExecutor(id, nomes) {
    return nomes.get(id) ?? id;
}
/**
 * Os ids internos no texto: viaturas (`V0167`), subcontratados (`S001`), motoristas (`M0048`) e
 * contas de pessoa (`U-leonel-pinheiro-excelvision-pt`).
 * ⚠️ Só se troca o que está no mapa de nomes: um `V1234` que não é viatura nenhuma fica como está.
 *
 * 🔴 2026-10-30 · A CONTA diz-se pelo NOME, como o motorista e a viatura. Visto na demonstração do
 *    Ctrl+Y: «Refeito: desfez um gesto em U-leonel-pinheiro-excelvision-pt|2026-10-30 …» — o id
 *    da conta deriva do email (`sessao.ts`), logo mostrá-lo é mostrar o email de uma pessoa
 *    (regra 7). O id é `U-` mais minúsculas, dígitos e hífenes; pode acabar num hífen (o id é
 *    cortado a 40 caracteres), e por isso o hífen final entra no casamento. E tem sempre uma
 *    letra (vem de um email): uma referência como `U-2026-ABC` não é uma conta.
 */
const ID_DE_EXECUTOR = /\b(?:V\d{4}|S\d{3}|M\d{4})\b|\bU-(?=[a-z0-9-]*[a-z])[a-z0-9]+(?:-[a-z0-9]+)*-?/g;
/** Troca, num texto, cada id de executor (e de conta) pelo nome que o planeador lê. */
export function trocarIdsDeExecutor(texto, nomes) {
    return texto.replace(ID_DE_EXECUTOR, (id) => {
        const nome = nomes.get(id);
        if (nome !== undefined)
            return nome;
        // Um id de conta seguido de um hífen do texto («U-ana-pt-…»): tenta sem o hífen final.
        if (id.startsWith('U-') && id.endsWith('-')) {
            const semHifen = nomes.get(id.slice(0, -1));
            if (semHifen !== undefined)
                return `${semHifen}-`;
        }
        return id;
    });
}
/** O texto tem algum id de executor? (para não pedir os nomes à base sem precisar) */
export function temIdDeExecutor(texto) {
    return new RegExp(ID_DE_EXECUTOR.source).test(texto);
}
/**
 * Os nomes dos executores a partir dos dados do mapa (o ecrã tem-nos sempre): a viatura pelo
 * número da primeira coluna, o subcontratado pelo nome, o motorista pelo nome que o mapa mostra
 * (`motoristas`, paralelo a `motoristasIds`). ⚠️ O mesmo número e o mesmo nome que o mapa pinta.
 */
export function nomesDosExecutoresDoMapa(dados) {
    const m = new Map();
    for (const v of dados.viaturas)
        if (v.numero !== '')
            m.set(v.id, v.numero);
    for (const s of dados.subcontratados ?? [])
        if (s.nome !== '')
            m.set(s.id, s.nome);
    (dados.motoristasIds ?? []).forEach((id, i) => {
        const nome = dados.motoristas?.[i] ?? '';
        if (id !== '' && nome !== '')
            m.set(id, nome);
    });
    return m;
}
//# sourceMappingURL=como-o-planeador-le.js.map